import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, X, Check, Search } from 'lucide-react';

export default function MultiSelect({
  options = [],
  selected = [],
  onChange,
  placeholder = "Select options...",
  label,
  required = false,
  minSelections = 0,
  error,
  isDarkMode = false,
  // Opt-in: matches the plain white/slate-300 field styling used elsewhere
  // (e.g. the SingleSelect dropdowns on the signup form), instead of this
  // component's own default grey/uppercase look.
  matchFieldStyle = false,
  // Opt-in: adds a filter input above the option list. Meant for large
  // option sets (e.g. 300+ cities) where scanning an unfiltered list isn't
  // practical.
  searchable = false,
  searchPlaceholder = "Search...",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) setSearchTerm('');
  }, [isOpen]);

  const toggleOption = (option) => {
    const optionValue = typeof option === 'string' ? option : option.value || option.id;
    const isSelected = selected.includes(optionValue);
    
    if (isSelected) {
      // Check minimum selections requirement
      if (selected.length <= minSelections) {
        return; // Don't allow deselecting if we're at minimum
      }
      onChange(selected.filter(item => item !== optionValue));
    } else {
      onChange([...selected, optionValue]);
    }
  };

  const removeOption = (optionValue, e) => {
    e.stopPropagation();
    if (selected.length <= minSelections) {
      return; // Don't allow removing if we're at minimum
    }
    onChange(selected.filter(item => item !== optionValue));
  };

  const getDisplayValue = (option) => {
    if (typeof option === 'string') return option;
    return option.label || option.name || option.value || option.id;
  };

  const getOptionValue = (option) => {
    if (typeof option === 'string') return option;
    return option.value || option.id || option.name;
  };

  const selectedOptions = options.filter(opt => selected.includes(getOptionValue(opt)));

  const visibleOptions = searchable && searchTerm.trim()
    ? options.filter(opt =>
        String(getDisplayValue(opt)).toLowerCase().includes(searchTerm.trim().toLowerCase())
      )
    : options;

  return (
    <div className="space-y-2">
      {label && (
        <label className={matchFieldStyle
          ? 'text-sm font-semibold mb-2 flex items-center'
          : `text-sm font-bold uppercase tracking-wide ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`
        }>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full p-3 rounded-xl border text-left flex items-center justify-between ${
            isDarkMode
              ? `bg-slate-700 border-slate-600 outline-none focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 ${error ? 'border-red-500' : ''}`
              : matchFieldStyle
                ? `transition-colors bg-white text-slate-900 ${error ? 'border-red-500' : 'border-slate-300'}`
                : `bg-slate-50 border-slate-200 outline-none focus:ring-4 focus:ring-blue-50 ${error ? 'border-red-300' : ''}`
          }`}
        >
          <div className="flex flex-wrap gap-2 flex-1">
            {selectedOptions.length > 0 ? (
              selectedOptions.map((option) => {
                const value = getOptionValue(option);
                return (
                  <span
                    key={value}
                    className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      isDarkMode 
                        ? 'bg-blue-900/50 text-blue-300' 
                        : 'bg-blue-100 text-blue-800'
                    }`}
                  >
                    {getDisplayValue(option)}
                    {selected.length > minSelections && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => removeOption(value, e)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            removeOption(value, e);
                          }
                        }}
                        className={`rounded-full p-0.5 cursor-pointer ${
                          isDarkMode 
                            ? 'hover:bg-blue-800' 
                            : 'hover:bg-blue-200'
                        }`}
                        aria-label="Remove option"
                      >
                        <X size={12} />
                      </span>
                    )}
                  </span>
                );
              })
            ) : (
              <span className={isDarkMode ? 'text-slate-400' : 'text-slate-400'}>{placeholder}</span>
            )}
          </div>
          <ChevronDown
            className={`ml-2 transition-transform ${isOpen ? 'rotate-180' : ''} ${
              isDarkMode ? 'text-slate-400' : 'text-slate-600'
            }`}
            size={20}
          />
        </button>

        {isOpen && (
          <div className={`absolute z-50 w-full mt-1 border rounded-xl shadow-lg ${
            isDarkMode
              ? 'bg-slate-800 border-slate-600'
              : 'bg-white border-slate-200'
          }`}>
            {searchable && (
              <div className={`sticky top-0 p-2 border-b ${
                isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-slate-200'
              }`}>
                <div className="relative">
                  <Search
                    size={16}
                    className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${
                      isDarkMode ? 'text-slate-500' : 'text-slate-400'
                    }`}
                  />
                  <input
                    type="text"
                    autoFocus
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    placeholder={searchPlaceholder}
                    className={`w-full pl-8 pr-3 py-2 rounded-lg text-sm outline-none border ${
                      isDarkMode
                        ? 'bg-slate-700 border-slate-600 text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-blue-500/40'
                        : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-blue-100'
                    }`}
                  />
                </div>
              </div>
            )}
            <div className="max-h-60 overflow-auto">
            {options.length === 0 ? (
              <div className={`p-3 text-sm text-center ${
                isDarkMode ? 'text-slate-400' : 'text-slate-500'
              }`}>No options available</div>
            ) : visibleOptions.length === 0 ? (
              <div className={`p-3 text-sm text-center ${
                isDarkMode ? 'text-slate-400' : 'text-slate-500'
              }`}>No matches found</div>
            ) : (
              visibleOptions.map((option) => {
                const value = getOptionValue(option);
                const isSelected = selected.includes(value);
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => toggleOption(option)}
                    className={`w-full px-4 py-2 text-left flex items-center justify-between ${
                      isDarkMode
                        ? `hover:bg-slate-700 ${isSelected ? 'bg-blue-900/30' : ''}`
                        : `hover:bg-slate-50 ${isSelected ? 'bg-blue-50' : ''}`
                    }`}
                    disabled={isSelected && selected.length <= minSelections}
                  >
                    <span className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>
                      {getDisplayValue(option)}
                    </span>
                    {isSelected && <Check size={16} className={isDarkMode ? 'text-blue-400' : 'text-blue-600'} />}
                  </button>
                );
              })
            )}
            </div>
          </div>
        )}
      </div>
      {error && <p className="text-sm text-red-500">{error}</p>}
      {minSelections > 0 && selected.length < minSelections && (
        <p className="text-sm text-amber-600">
          Please select at least {minSelections} option{minSelections > 1 ? 's' : ''}
        </p>
      )}
    </div>
  );
}

